// src/pages/EditRentalCarPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp, collection, writeBatch, getDocs, arrayUnion } from 'firebase/firestore';
import { FaTools, FaDollarSign, FaUser, FaStickyNote, FaSpinner } from 'react-icons/fa';

const EditRentalCarPage = () => {
    const { profileId } = useParams();
    const { currentUser, userData } = useAuth();
    const navigate = useNavigate();

    const [formData, setFormData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    // State for the "Start Rental" form
    const [customers, setCustomers] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState('');
    const [totalPrice, setTotalPrice] = useState('');
    const [paidAmount, setPaidAmount] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('Nakit');
    const [paymentSource, setPaymentSource] = useState('');
    const [rentalEndDate, setRentalEndDate] = useState('');

    const fetchData = useCallback(async () => {
        if (!userData?.companyProfileId) return;
        setLoading(true);
        try {
            const docRef = doc(db, 'vehicleProfiles', profileId);
            const docSnap = await getDoc(docRef);

                                        if (docSnap.exists() && docSnap.data().userId === currentUser.uid) {
                                            let data = docSnap.data();
                                            if (!data.internalData) data.internalData = { status: 'Müsait', notes: '', currentRentalId: null, kaskoExpireDate: '', sigortaExpireDate: '', yagBakimTarihi: '', muayeneGecerlilikTarihi: '', preNotesDriver: '', preNotesAvailable: '', isNoted: false };
                            
                                            // Ensure renterInfo and its nested properties are initialized
                                            if (!data.renterInfo) data.renterInfo = {};
                                            if (!data.renterInfo.name) data.renterInfo.name = { value: '', isVisible: true };
                                            if (!data.renterInfo.phone) data.renterInfo.phone = { value: '', isVisible: true };
                            
                                            // Ensure availabilityInfo and its nested properties are initialized
                                            if (!data.availabilityInfo) data.availabilityInfo = {};
                                            if (!data.availabilityInfo.price) data.availabilityInfo.price = { value: '', isVisible: true };
                                            if (!data.availabilityInfo.note) data.availabilityInfo.note = { value: '', isVisible: true };
                            
                                            // Fetch company contact info
                                            const companyDocRef = doc(db, 'companies', userData.companyProfileId);
                                            const companySnap = await getDoc(companyDocRef);
                                            if (companySnap.exists()) {
                                                const companyData = companySnap.data();
                                                data.companyWebsite = companyData.companyWebsite || { value: '', isVisible: true };
                                                data.companyContactPhone = companyData.companyContactPhone || { value: '', isVisible: true };
                                                data.companyInstagram = companyData.companyInstagram || { value: '', isVisible: true };
                                                data.companyWhatsapp = companyData.companyWhatsapp || { value: '', isVisible: true };
                                            }
                            
                                            setFormData(data);                const customersCollectionRef = collection(db, 'companies', userData.companyProfileId, 'customers');
                const customerData = await getDocs(customersCollectionRef);
                setCustomers(customerData.docs.map(doc => ({ ...doc.data(), id: doc.id })));
            } else {
                setError("Profil bulunamadı veya bu profili düzenleme yetkiniz yok.");
                navigate('/company-dashboard');
            }
        } catch (err) {
            setError("Veri yüklenirken bir hata oluştu.");
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [currentUser, profileId, navigate, userData]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleInternalChange = (field, value) => {
        setFormData(prev => ({ ...prev, internalData: { ...prev.internalData, [field]: value } }));
    };

    const handleSaveInternalData = async () => {
        setSaving(true);
        const docRef = doc(db, 'vehicleProfiles', profileId);
        
        let updateData = { 
            vehiclePlate: formData.vehiclePlate,
            "internalData.status": formData.internalData.status,
            "internalData.notes": formData.internalData.notes,
            "internalData.kaskoExpireDate": formData.internalData.kaskoExpireDate,
            "internalData.sigortaExpireDate": formData.internalData.sigortaExpireDate,
            "internalData.yagBakimTarihi": formData.internalData.yagBakimTarihi,
            "internalData.muayeneGecerlilikTarihi": formData.internalData.muayeneGecerlilikTarihi,
            "availabilityInfo.note": formData.availabilityInfo.note,
            "renterInfo.name.isVisible": formData.renterInfo.name.isVisible,
            "renterInfo.phone.isVisible": formData.renterInfo.phone.isVisible,
            updatedAt: serverTimestamp() 
        };

        if (formData.internalData.notes && formData.internalData.notes.trim() !== "") {
            updateData["noteHistory"] = arrayUnion({
                text: formData.internalData.notes,
                date: new Date().toISOString(),
                type: 'Note'
            });
        }

        await updateDoc(docRef, updateData);
        setSaving(false);
        alert('Durum ve notlar kaydedildi!');
        setFormData(prev => ({...prev, internalData: {...prev.internalData, status: formData.internalData.status, notes: formData.internalData.notes, kaskoExpireDate: formData.internalData.kaskoExpireDate, sigortaExpireDate: formData.internalData.sigortaExpireDate, yagBakimTarihi: formData.internalData.yagBakimTarihi, muayeneGecerlilikTarihi: formData.internalData.muayeneGecerlilikTarihi}}));
    };


    const handleSavePreNotes = async () => {
        setSaving(true);
        const docRef = doc(db, 'vehicleProfiles', profileId);
        await updateDoc(docRef, {
            "internalData.preNotesDriver": formData.internalData.preNotesDriver,
            "internalData.preNotesAvailable": formData.internalData.preNotesAvailable,
            "internalData.isNoted": true,
            updatedAt: serverTimestamp()
        });
        setSaving(false);
        alert('Ön notlar kaydedildi!');
        setFormData(prev => ({...prev, internalData: {...prev.internalData, isNoted: true}}));

    };

    const handleClearPreNotes = async () => {
        setSaving(true);
        const docRef = doc(db, 'vehicleProfiles', profileId);
        await updateDoc(docRef, {
            "internalData.preNotesDriver": "",
            "internalData.preNotesAvailable": "",
            "internalData.isNoted": false,
            updatedAt: serverTimestamp()
        });
        setSaving(false);
        alert('Ön notlar temizlendi!');
        setFormData(prev => ({...prev, internalData: {...prev.internalData, preNotesDriver: '', preNotesAvailable: '', isNoted: false}}));
    };
    
    
    const handleStartRental = async () => {
        const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
        if (!selectedCustomer || totalPrice <= 0 || !rentalEndDate) {
            alert("Lütfen bir müşteri seçin, geçerli bir ücret ve bitiş tarihi girin.");
            return;
        }
        setSaving(true);
        const batch = writeBatch(db);
        const rentalRef = doc(collection(db, 'rentals'));

        batch.set(rentalRef, {
            companyId: userData.companyProfileId,
            vehicleProfileId: profileId,
            vehiclePlate: formData.vehiclePlate,
            renterName: selectedCustomer.name,
            renterPhone: selectedCustomer.phone,
            totalPrice: Number(totalPrice),
            paidAmount: Number(paidAmount) || 0,
            paymentMethod: paymentMethod,
            paymentSource: paymentSource || 'Belirtilmedi',
            paymentStatus: (Number(paidAmount) || 0) >= Number(totalPrice) ? "Ödendi" : (Number(paidAmount) > 0 ? "Kısmi Ödendi" : "Ödenmedi"),
            rentalStartDate: serverTimestamp(),
            rentalEndDate: new Date(rentalEndDate),
        });

        const vehicleDocRef = doc(db, 'vehicleProfiles', profileId);
        batch.update(vehicleDocRef, {
            vehiclePlate: formData.vehiclePlate,
            isRented: true,
            "internalData.status": "Kirada",
            "internalData.currentRentalId": rentalRef.id,
            "renterInfo.name.value": selectedCustomer.name,
            "renterInfo.phone.value": selectedCustomer.phone,
            "renterInfo.name.isVisible": true,
            "renterInfo.phone.isVisible": true,
            "internalData.isNoted": false,
        });
        
        await batch.commit();
        setSaving(false);
        fetchData();
    };

    const handleEndRental = async () => {
        if (!window.confirm("Bu aracın kiralamasını bitirmek istediğinizden emin misiniz?")) return;
        
        let finalPayment = 0;
        let finalMethod = "";
        
        // Calculate debt? We don't have the rental doc in state fully (only formData.internalData.currentRentalId).
        // Fetch it first to be sure or just ask the user.
        // Let's ask the user for a final "Closing Payment" if any.
        const paymentInput = window.prompt("Varsa tahsil edilen son ödeme tutarını girin (Yoksa 0 yazın):", "0");
        if (paymentInput === null) return; // Cancelled
        finalPayment = Number(paymentInput) || 0;

        if (finalPayment > 0) {
            finalMethod = window.prompt("Ödeme Yöntemi (Nakit, Kredi Kartı, vb.):", "Nakit");
        }

        setSaving(true);
        const batch = writeBatch(db);
        
        const vehicleDocRef = doc(db, 'vehicleProfiles', profileId);
        
        // Update Vehicle Profile
        batch.update(vehicleDocRef, {
            isRented: false,
            "internalData.status": "Müsait",
            "internalData.currentRentalId": null,
            "renterInfo.name.value": "",
            "renterInfo.phone.value": "",
            "renterInfo.name.isVisible": false,
            "renterInfo.phone.isVisible": false,
            "internalData.isNoted": false,
            // Add completion note to history
            "noteHistory": arrayUnion({
                text: `Kiralama Tamamlandı. ${finalPayment > 0 ? `Son Tahsilat: ${finalPayment} TL (${finalMethod})` : 'Ek tahsilat yok.'}`,
                date: new Date().toISOString(),
                type: 'System'
            })
        });

        if (formData.internalData.currentRentalId) {
            const rentalDocRef = doc(db, 'rentals', formData.internalData.currentRentalId);
            batch.update(rentalDocRef, { 
                rentalEndDate: serverTimestamp(),
                finalPaymentAmount: finalPayment,
                finalPaymentMethod: finalMethod,
                status: 'Completed',
                completionNote: `Rent tamamlandı. ${finalPayment > 0 ? `+${finalPayment} TL` : ''}`
            });
        }
        
        await batch.commit();
        setSaving(false);
        fetchData();
    };

    if (loading || !formData) {
        return <div className="text-center p-10"><FaSpinner className="animate-spin inline-block mr-2" size={24} /> Yükleniyor...</div>;
    }
    if (error) return <div className="text-red-500 p-4">{error}</div>;

    return (
        <div className="container mx-auto max-w-4xl p-4 sm:p-8">
            <Link to="/company-dashboard" className="text-blue-600 hover:underline mb-4 inline-block">&larr; Filo Paneline Dön</Link>
            <h1 className="text-3xl font-bold">{formData.vehiclePlate || "Plakasız Araç"}</h1>
            <p className="font-mono text-gray-500 mb-6">{profileId}</p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {/* Left Column: Management */}
                <div className="md:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h2 class="text-xl font-bold mb-4 flex items-center"><FaTools className="mr-2"/> Araç Yönetimi (Şirket İçi)</h2>
                        <div className="mb-4">
                            <label className="block font-medium mb-1">Araç Plakası</label>
                            <input 
                                type="text" 
                                value={formData.vehiclePlate || ''} 
                                onChange={(e) => setFormData(prev => ({ ...prev, vehiclePlate: e.target.value }))} 
                                className="w-full border p-2 rounded bg-gray-50" 
                                placeholder="34 ABC 123"
                            />
                        </div>
                        {(formData.internalData.preNotesDriver || formData.internalData.preNotesAvailable) && (
                            <div className="mb-4 p-4 bg-yellow-100 rounded-lg">
                                <h3 class="font-bold text-yellow-800">Ön Notlar</h3>
                                {formData.internalData.preNotesDriver && <p className="text-sm text-yellow-700"><strong>Sürücü için:</strong> {formData.internalData.preNotesDriver}</p>}
                                {formData.internalData.preNotesAvailable && <p className="text-sm text-yellow-700"><strong>Müsaitlik için:</strong> {formData.internalData.preNotesAvailable}</p>}
                            </div>
                        )}
                        <div className="mb-4">
                            <label className="block font-medium mb-1">Araç Durumu</label>
                            <select value={formData.internalData.status} onChange={(e) => handleInternalChange('status', e.target.value)} className="w-full border p-2 rounded bg-gray-50">
                                <option>Müsait</option>
                                <option>Kirada</option>
                                <option>Bakımda</option>
                                <option>Hasarlı</option>
                                <option>Teslimatta</option>
                            </select>
                        </div>
                        <div>
                            <label className="block font-medium mb-1">İç Notlar (Sadece size görünür)</label>
                            <textarea value={formData.internalData.notes} onChange={(e) => handleInternalChange('notes', e.target.value)} rows="4" className="w-full border p-2 rounded bg-gray-50" placeholder="Örn: Sol ön lastik yeni değişti."></textarea>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                            <div>
                                <label className="block font-medium mb-1">Kasko Bitiş Tarihi</label>
                                <input type="date" value={formData.internalData.kaskoExpireDate || ''} onChange={(e) => handleInternalChange('kaskoExpireDate', e.target.value)} className="w-full border p-2 rounded bg-gray-50" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Sigorta Bitiş Tarihi</label>
                                <input type="date" value={formData.internalData.sigortaExpireDate || ''} onChange={(e) => handleInternalChange('sigortaExpireDate', e.target.value)} className="w-full border p-2 rounded bg-gray-50" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Yağ Bakım Tarihi</label>
                                <input type="date" value={formData.internalData.yagBakimTarihi || ''} onChange={(e) => handleInternalChange('yagBakimTarihi', e.target.value)} className="w-full border p-2 rounded bg-gray-50" />
                            </div>
                            <div>
                                <label className="block font-medium mb-1">Muayene Geçerlilik Tarihi</label>
                                <input type="date" value={formData.internalData.muayeneGecerlilikTarihi || ''} onChange={(e) => handleInternalChange('muayeneGecerlilikTarihi', e.target.value)} className="w-full border p-2 rounded bg-gray-50" />
                            </div>
                        </div>
                        <div class="mt-4">
                            <label className="block font-medium mb-1">Sürücü için Ön Notlar</label>
                            <textarea value={formData.internalData.preNotesDriver} onChange={(e) => handleInternalChange('preNotesDriver', e.target.value)} rows="2" className="w-full border p-2 rounded bg-gray-50" placeholder="Araç bir sonraki sürücüye teslim edildiğinde gösterilecek notlar."></textarea>
                        </div>
                        <div class="mt-4">
                            <label className="block font-medium mb-1">Müsaitlik için Ön Notlar</label>
                            <textarea value={formData.internalData.preNotesAvailable} onChange={(e) => handleInternalChange('preNotesAvailable', e.target.value)} rows="2" className="w-full border p-2 rounded bg-gray-50" placeholder="Araç müsait olduğunda görülecek notlar."></textarea>
                        </div>

                         <button onClick={handleSaveInternalData} disabled={saving} className="mt-4 w-full bg-blue-600 text-white py-2 rounded hover:bg-blue-700 transition-colors">
                            Durum ve Notları Kaydet
                        </button>
                        <button onClick={handleSavePreNotes} disabled={saving} className="mt-2 w-full bg-orange-600 text-white py-2 rounded hover:bg-orange-700 transition-colors">
                            Ön Notları Kaydet
                        </button>
                        <button onClick={handleClearPreNotes} disabled={saving} className="mt-2 w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors">
                            Ön Notları Temizle
                        </button>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-md">
                        <h2 class="text-xl font-bold mb-4 flex items-center"><FaStickyNote className="mr-2"/> QR Kod Görünümü</h2>
                        <div className="mb-4">
                            <label className="block font-medium mb-1">Görünür Not</label>
                            <textarea value={formData.availabilityInfo.note.value} onChange={(e) => setFormData(prev => ({ ...prev, availabilityInfo: { ...prev.availabilityInfo, note: { ...prev.availabilityInfo.note, value: e.target.value } } }))} rows="3" className="w-full border p-2 rounded bg-gray-50" placeholder="QR kodu okutulduğunda görünecek not."></textarea>
                        </div>
                        <div className="flex items-center mb-4">
                            <input type="checkbox" checked={formData.availabilityInfo.note.isVisible} onChange={(e) => setFormData(prev => ({ ...prev, availabilityInfo: { ...prev.availabilityInfo, note: { ...prev.availabilityInfo.note, isVisible: e.target.checked } } }))} id="isNoteVisible" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label htmlFor="isNoteVisible" className="ml-2 block text-sm text-gray-900">Notu göster</label>
                        </div>
                        <div className="flex items-center mb-4">
                            <input type="checkbox" checked={formData.renterInfo.name.isVisible} onChange={(e) => setFormData(prev => ({ ...prev, renterInfo: { ...prev.renterInfo, name: { ...prev.renterInfo.name, isVisible: e.target.checked } } }))} id="isNameVisible" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label htmlFor="isNameVisible" className="ml-2 block text-sm text-gray-900">Sürücü adını göster</label>
                        </div>
                        <div className="flex items-center">
                            <input type="checkbox" checked={formData.renterInfo.phone.isVisible} onChange={(e) => setFormData(prev => ({ ...prev, renterInfo: { ...prev.renterInfo, phone: { ...prev.renterInfo.phone, isVisible: e.target.checked } } }))} id="isPhoneVisible" className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" />
                            <label htmlFor="isPhoneVisible" className="ml-2 block text-sm text-gray-900">Sürücü telefonunu göster</label>
                        </div>
                    </div>
                </div>

                {/* Right Column: Rental Actions */}
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h2 className="text-xl font-bold mb-4 flex items-center"><FaDollarSign className="mr-2"/> Kiralama İşlemleri</h2>
                    {formData.isRented ? (
                        <div className="text-center">
                            <FaUser size={40} className="mx-auto text-gray-400 mb-4"/>
                            <p className="mb-2">Bu araç şu anda kirada.</p>
                            <p className="font-bold text-lg">{formData.renterInfo.name.value}</p>
                            <button onClick={handleEndRental} disabled={saving} className="mt-6 w-full bg-red-600 text-white py-2 rounded hover:bg-red-700 transition-colors">
                                {saving ? 'İşleniyor...' : 'Kiralamayı Bitir'}
                            </button>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <p className="text-sm text-gray-600">Yeni bir kiralama başlatmak için müşteri seçin.</p>
                            <div>
                                <label className="block font-medium">Müşteri Seç</label>
                                <select value={selectedCustomerId} onChange={(e) => setSelectedCustomerId(e.target.value)} className="w-full border p-2 rounded bg-gray-50">
                                    <option value="">-- Müşteri Seçin --</option>
                                    {customers.map(customer => (
                                        <option key={customer.id} value={customer.id}>{customer.name} - {customer.phone}</option>
                                    ))}
                                </select>
                                <Link to="/company/customers" className="text-xs text-blue-600 hover:underline mt-1 inline-block">Yeni Müşteri Ekle</Link>
                            </div>
                            <div>
                                <label className="block font-medium">Toplam Kiralama Ücreti (TL)</label>
                                <input type="number" value={totalPrice} onChange={(e) => setTotalPrice(e.target.value)} className="w-full border p-2 rounded" placeholder="Örn: 3000" />
                            </div>
                            <div>
                                <label className="block font-medium">Alınan Kapora/Ödeme (TL)</label>
                                <input type="number" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} className="w-full border p-2 rounded" placeholder="Örn: 500" />
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <div>
                                    <label className="block font-medium">Ödeme Yöntemi</label>
                                    <select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} className="w-full border p-2 rounded bg-gray-50">
                                        <option value="Nakit">Nakit</option>
                                        <option value="Kredi Kartı">Kredi Kartı</option>
                                        <option value="Havale/EFT">Havale/EFT</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block font-medium">Ödeme Kaynağı</label>
                                    <input type="text" value={paymentSource} onChange={(e) => setPaymentSource(e.target.value)} className="w-full border p-2 rounded" placeholder="Örn: Garanti Bankası" />
                                </div>
                            </div>
                            <div>
                                <label className="block font-medium">Rental End Date</label>
                                <input type="datetime-local" value={rentalEndDate} onChange={(e) => setRentalEndDate(e.target.value)} className="w-full border p-2 rounded" />
                            </div>
                            <button onClick={handleStartRental} disabled={saving} className="w-full bg-green-600 text-white py-2 rounded hover:bg-green-700 transition-colors">
                                {saving ? 'İşleniyor...' : 'Kiralamayı Başlat'}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default EditRentalCarPage;